if (!customElements.get('happy-families-component')) {
  class HappyFamiliesSlider extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      // Safari/iOS autoplay + HLS handling is stricter; detect iOS once.
      const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      this.isIOS = isIOS;

      this.initReadMoreModal = () => {};

      this.initSoundToggles = () => {
        const setButtonState = (button, isUnmuted) => {
          if (!button) return;
          button.classList.toggle('is-unmuted', isUnmuted);
          button.setAttribute('aria-pressed', isUnmuted ? 'true' : 'false');
          button.setAttribute('aria-label', isUnmuted ? 'Mute video' : 'Unmute video');
        };

        const getOrMountPlayableFromMedia = async (mediaEl) => {
          if (!mediaEl) return null;

          // Direct <video> (HLS native video, or already-mounted theme video)
          let video = mediaEl.querySelector('video');
          if (video) return { type: 'video', el: video };

          // External embeds (YouTube/Vimeo) might be mounted already.
          let iframe = mediaEl.querySelector('iframe');
          if (iframe) {
            const src = iframe.getAttribute('src') || '';
            if (src.includes('youtube.com/embed/')) return { type: 'youtube', el: iframe };
            if (src.includes('player.vimeo.com/video/')) return { type: 'vimeo', el: iframe };
            return { type: 'iframe', el: iframe };
          }

          // Theme deferred media often uses <video-element> that mounts <video> after poster click.
          const videoElement = mediaEl.querySelector('video-element');
          if (videoElement) {
            if (videoElement.dataset.hfMounted !== 'true') {
              videoElement.dataset.hfMounted = 'true';
              try {
                // Avoid poster click toggling on iOS; initialize/play directly.
                videoElement.paused = false;
                videoElement.play();
              } catch (e) {}
            }

            // Wait briefly for <video> or <iframe> to appear in light DOM.
            for (let i = 0; i < 15; i++) {
              await new Promise((r) => setTimeout(r, 40));
              video = mediaEl.querySelector('video');
              if (video) return { type: 'video', el: video };

              iframe = mediaEl.querySelector('iframe');
              if (iframe) {
                const src = iframe.getAttribute('src') || '';
                if (src.includes('youtube.com/embed/')) return { type: 'youtube', el: iframe };
                if (src.includes('player.vimeo.com/video/')) return { type: 'vimeo', el: iframe };
                return { type: 'iframe', el: iframe };
              }
            }
          }

          return null;
        };

        const forceMuted = (video) => {
          if (!video) return;
          video.dataset.hfUserUnmuted = 'false';
          video.muted = true;

          try {
            video.volume = 0;
          } catch (e) {}
          try {
            video.setAttribute('muted', '');
          } catch (e) {}
          video.removeAttribute('controls');
        };

        // Default state on load: muted + no native controls.
        this.querySelectorAll('video').forEach((video) => forceMuted(video));
        this.querySelectorAll('.happy-families-sound-toggle').forEach((btn) => setButtonState(btn, false));

        const onSoundToggleClick = async (event) => {
          const button = event.currentTarget;
          if (!button || !this.contains(button)) return;

          event.preventDefault();
          event.stopPropagation();
          if (event.stopImmediatePropagation) event.stopImmediatePropagation();

          const media = button.closest('.happy-families-card__media') || button.parentElement;
          const playable = await getOrMountPlayableFromMedia(media);
          if (!playable) return;

          // Toggle
          if (playable.type === 'video') {
            const videoEl = playable.el;

            // Make sure the HLS video has an attached src before attempting play.
            await this.ensureVideoSource(videoEl);

            const nextUnmuted = videoEl.muted === true;
            if (nextUnmuted) {
              videoEl.dataset.hfUserUnmuted = 'true';
              videoEl.muted = false;
              videoEl.defaultMuted = false;
              try {
                videoEl.volume = 1;
              } catch (e) {}
              try {
                videoEl.removeAttribute('muted');
              } catch (e) {}
            } else {
              videoEl.dataset.hfUserUnmuted = 'false';
              videoEl.muted = true;
              videoEl.defaultMuted = true;
              try {
                videoEl.volume = 0;
              } catch (e) {}
              try {
                videoEl.setAttribute('muted', '');
              } catch (e) {}
            }
            videoEl.removeAttribute('controls');

            if (nextUnmuted) {
              try {
                // Always attempt a user-gesture play on unmute for Safari/iOS reliability.
                await videoEl.play();
              } catch (e) {}
            }

            setButtonState(button, nextUnmuted);
            return;
          }

          // External videos (YouTube/Vimeo)
          const iframe = playable.el;
          const nextUnmuted = button.getAttribute('aria-pressed') !== 'true';

          if (playable.type === 'youtube') {
            // YouTube IFrame API command interface (enablejsapi=1 in src).
            try {
              iframe.contentWindow?.postMessage(
                JSON.stringify({ event: 'command', func: nextUnmuted ? 'unMute' : 'mute', args: [] }),
                '*'
              );
              if (nextUnmuted) {
                iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'playVideo', args: [] }), '*');
              }
            } catch (e) {}
            setButtonState(button, nextUnmuted);
            return;
          }

          if (playable.type === 'vimeo') {
            // Vimeo player postMessage API.
            try {
              iframe.contentWindow?.postMessage({ method: 'setVolume', value: nextUnmuted ? 1 : 0 }, '*');
              if (nextUnmuted) {
                iframe.contentWindow?.postMessage({ method: 'play' }, '*');
              }
            } catch (e) {}
            setButtonState(button, nextUnmuted);
            return;
          }

          // Unknown iframe: we can only flip button state.
          setButtonState(button, nextUnmuted);
          return;
        };

        this.querySelectorAll('.happy-families-sound-toggle').forEach((button) => {
          if (button.dataset.hfSoundBound === 'true') return;
          button.dataset.hfSoundBound = 'true';
          // Prevent swiper/drag handlers from stealing the tap.
          button.addEventListener('pointerdown', (event) => {
            event.stopPropagation();
          });
          button.addEventListener('touchstart', (event) => {
            event.stopPropagation();
          });
          button.addEventListener('click', onSoundToggleClick);
          button.addEventListener('touchend', onSoundToggleClick);
        });
      };

      this.pauseAllVideos = (exceptVideo = null) => {
        const videos = this.querySelectorAll('video');
        videos.forEach((video) => {
          if (video === exceptVideo) return;
          if (!video.paused) video.pause();
        });
      };
      this.prepareVideoForAutoplay = (video) => {
        if (!video) return;
        // iOS Safari autoplay requirements.
        const userUnmuted = video.dataset && video.dataset.hfUserUnmuted === 'true';
        if (!userUnmuted) {
          video.muted = true;
          video.defaultMuted = true;
          video.setAttribute('muted', '');
        } else {
          video.muted = false;
          video.defaultMuted = false;
          video.removeAttribute('muted');
        }
        video.playsInline = true;
        video.preload = 'auto';
        video.loop = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('autoplay', '');
        video.setAttribute('preload', 'auto');
        video.setAttribute('loop', '');
      };
      this.ensureVideoSource = async (video) => {
        if (!video) return;

        // Only handle HLS-mapped videos used in this section.
        const hlsSrc = video.getAttribute('data-hls-src') || video.getAttribute('data-original-src');
        if (!hlsSrc) return;

        const timeoutMs = 6000;

        this.prepareVideoForAutoplay(video);

        const waitForMetadata = () =>
          new Promise((resolve) => {
            if (video.readyState >= 2) return resolve();
            let done = false;
            const finish = () => {
              if (done) return;
              done = true;
              resolve();
            };
            video.addEventListener('loadedmetadata', finish, { once: true });
            video.addEventListener('canplay', finish, { once: true });
            video.addEventListener('error', finish, { once: true });
            setTimeout(finish, timeoutMs);
          });

        // iOS Safari can play native HLS. Be aggressive: force native src assignment.
        try {
          const canPlayHls = typeof video.canPlayType === 'function' && video.canPlayType('application/vnd.apple.mpegurl');
          if (isIOS || canPlayHls) {
            if (video.src !== hlsSrc) {
              video.src = hlsSrc;
            }
            try {
              video.load();
            } catch (e) {}

            await waitForMetadata();
            return;
          }
        } catch (e) {}

        // For non-native browsers: wait briefly for the global `hls-video-init.js`
        // to attach sources. This avoids racing Safari, while keeping Chrome behavior.
        await new Promise((resolve) => {
          const start = Date.now();
          const poll = () => {
            if (video.readyState >= 2) return resolve();
            if (video.src) return resolve();
            if (Date.now() - start > timeoutMs) return resolve();
            setTimeout(poll, 100);
          };
          poll();
        });
      };

      this._playRequestId = 0;
      this.playVideosIn = async (container) => {
        if (!container) return;

        const requestId = ++this._playRequestId;

        // If this is a deferred <video-element>, initialize playback without toggling it off.
        const videoElements = container.querySelectorAll('video-element');
        videoElements.forEach((videoElement) => {
          if (videoElement.hasAttribute('loaded')) return;

          // Initialize directly to avoid poster toggle regressions on iOS Safari.
          if (videoElement.dataset.hfMounted !== 'true') {
            videoElement.dataset.hfMounted = 'true';
            try {
              videoElement.paused = false;
              videoElement.play();
            } catch (e) {}
          }
        });

        // Give deferred media a tick to mount.
        await new Promise((r) => setTimeout(r, 0));

        let videos = Array.from(container.querySelectorAll('video'));
        // Wait briefly for deferred <video-element> to mount real <video>.
        for (let i = 0; i < 10 && videos.length === 0; i++) {
          await new Promise((r) => setTimeout(r, 40));
          videos = Array.from(container.querySelectorAll('video'));
        }

        if (!videos.length) return;
        // Ensure HLS src is attached before attempting playback.
        await Promise.all(videos.map((video) => this.ensureVideoSource(video)));

        // If the slide changed again while we were waiting, don't start playback.
        if (requestId !== this._playRequestId) return;

        videos.forEach((video) => {
          this.prepareVideoForAutoplay(video);
          const tryPlay = () =>
            video.play().catch(() => {
              // Safari sometimes rejects early; retry once after a short delay.
              if (requestId !== this._playRequestId) return;
              setTimeout(() => {
                if (requestId !== this._playRequestId) return;
                this.prepareVideoForAutoplay(video);
                if (video && video.paused) video.play().catch(() => {});
              }, 250);
            });

          if (video.readyState >= 2) {
            tryPlay();
          } else {
            const onCanPlay = () => {
              video.removeEventListener('canplay', onCanPlay);
              if (requestId !== this._playRequestId) return;
              this.prepareVideoForAutoplay(video);
              tryPlay();
            };
            video.addEventListener('canplay', onCanPlay, { once: true });
            // Safari sometimes rejects early; retry once after a short delay.
            setTimeout(() => {
              if (requestId !== this._playRequestId) return;
              if (video.paused) {
                this.prepareVideoForAutoplay(video);
                tryPlay();
              }
            }, 500);
          }
        });
      };

      this.selectors = {
        sliderWrapper: '.testimonials__items',
        pagination: '.swiper-pagination',
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      };
      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

      this.enableSlider = this.dataset.enableSlider === 'true';
      this.items = parseFloat(this.dataset.items);
      this.laptopItems = parseFloat(this.dataset.laptopItems);
      this.tabletItems = parseFloat(this.dataset.tabletItems);
      this.swipeMobile = this.dataset.swipeMobile === 'true';

      this.sliderInstance = false;

      // When any video inside this component starts playing, pause all others in this section.
      this.addEventListener(
        'play',
        (event) => {
          const target = event.target;
          if (!target || target.tagName !== 'VIDEO') return;
          this.pauseAllVideos(target);
        },
        true
      );

      if (!this.enableSlider) {
        this.playFirstSlideVideo();
      } else {
        this.init();
        document.addEventListener('matchMobile', () => {
          this.init();
        });
        document.addEventListener('unmatchMobile', () => {
          this.init();
        });
      }

      this.initSoundToggles();
      // read more disabled

      // iOS can miss first autoplay on very early paint; retry once page is visible/fully loaded.
      if (this.isIOS) {
        const replayFirst = () => {
          if (document.visibilityState === 'hidden') return;
          this.playFirstSlideVideo();
        };
        setTimeout(replayFirst, 250);
        setTimeout(replayFirst, 900);
        setTimeout(replayFirst, 1600);
        window.addEventListener('load', replayFirst, { once: true });
        window.addEventListener('pageshow', replayFirst, { passive: true });
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') replayFirst();
        });
      }
    }

    init() {
      this.initSlider();
    }

    initSlider() {
      const columnGap = window
        .getComputedStyle(this.sliderWrapper)
        .getPropertyValue('--f-column-gap');
      const columnGapMobile = window
        .getComputedStyle(this.sliderWrapper)
        .getPropertyValue('--f-column-gap-mobile');
      const spaceBetween = parseFloat(columnGap.replace('rem', '')) * 10;
      const spaceBetweenMobile = parseFloat(columnGapMobile.replace('rem', '')) * 10;

      this.defaultOptions = {
        // On mobile, show ~1.2 slides (peek next card)
        slidesPerView: 1.2,
        // spaceBetween: spaceBetweenMobile,
        centeredSlides: false,
        centeredSlidesBounds: false,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        pagination: {
          el: this.section.querySelector(this.selectors.pagination),
          clickable: true,
        },
        breakpoints: {
          768: {
            slidesPerView: 1.5,
            // spaceBetween: spaceBetween,
            centeredSlides: false, // Ensure false on larger screens too
          },
          1024: {
            slidesPerView: 2.5,
            // spaceBetween: spaceBetween,
          },
          1280: {
            slidesPerView: 2.5,
            // spaceBetween: spaceBetween,
          },
        },
        loop: false,
        threshold: 2,
      };

      if (typeof this.sliderInstance !== 'object') {
        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);
        this.sliderInstance = new window.FoxTheme.Carousel(this, this.defaultOptions);
        this.sliderInstance.init();

        const swiper = this.sliderInstance.slider;

        const playActiveSlideVideo = () => {
          if (!swiper || !swiper.slides) return;

          this.pauseAllVideos();

          const activeSlide = swiper.slides[swiper.activeIndex];
          if (!activeSlide) return;

          // Fire and forget: internal logic waits for HLS src readiness.
          void this.playVideosIn(activeSlide);
        };
        const queuePlayActiveSlideVideo = () => {
          // Wait for transform/layout to settle on mobile before playing.
          setTimeout(() => playActiveSlideVideo(), 60);
        };

        // First slide
        queuePlayActiveSlideVideo();
        if (this.isIOS) {
          setTimeout(queuePlayActiveSlideVideo, 450);
          setTimeout(queuePlayActiveSlideVideo, 1200);
        }

        swiper.on('slideChange', queuePlayActiveSlideVideo);
        swiper.on('slideChangeTransitionEnd', queuePlayActiveSlideVideo);
        swiper.on('transitionEnd', queuePlayActiveSlideVideo);
      }
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);
      if (typeof this.sliderInstance === 'object') {
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }
      this.playFirstSlideVideo();
    }

    playFirstSlideVideo() {
      this.pauseAllVideos();

      const firstSlide = this.sliderWrapper.querySelector('.swiper-slide, .f-column');
      if (!firstSlide) return;

      void this.playVideosIn(firstSlide);
    }

  }

  customElements.define('happy-families-component', HappyFamiliesSlider);
}

